import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, ArrowUpRight, Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/motion";
import { SectionLabel, PageHero } from "../components/common";
import { CONTACT, DEPARTMENTS } from "../data/site";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", company: "", country: "", email: "", phone: "", department: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Thank you — we will be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "rounded-none border-slate-300 focus-visible:ring-brand-800 focus-visible:ring-offset-0 h-12";

  return (
    <>
      <Seo
        title="Contact | Talk to Our Engineering Team — STERILTRON"
        description="Contact STERILTRON in Rétság, Hungary. Talk to our engineering team about medical waste treatment and sterilization requirements for your facility."
        path="/contact"
      />
      <PageHero
        label="Contact"
        titleLines={["Talk to Our", "Engineering Team"]}
        description="Tell us about your facility and requirements. Our engineers will help specify the right STERILTRON solution for your operation."
      />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Info column */}
          <div className="lg:col-span-4">
            <SectionLabel>Get in touch</SectionLabel>
            <h2 className="mt-6 font-heading text-3xl font-bold tracking-tighter text-brand-950">STERILTRON</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Hungary-based engineering, operating internationally.</p>

            <div className="mt-10 space-y-6">
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-start gap-4 group">
                <span className="w-11 h-11 bg-brand-800 text-white flex items-center justify-center flex-shrink-0"><Mail size={18} /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-slate-400">Email</span>
                  <span className="block text-brand-900 font-medium group-hover:text-brand-700 transition-colors">{CONTACT.email}</span>
                </span>
              </a>
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 bg-brand-800 text-white flex items-center justify-center flex-shrink-0"><MapPin size={18} /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-slate-400">Address</span>
                  <span className="block text-brand-900 font-medium">{CONTACT.address}</span>
                  <span className="block text-slate-600">{CONTACT.city}</span>
                </span>
              </div>
              <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-start gap-4 group">
                <span className="w-11 h-11 bg-brand-800 text-white flex items-center justify-center flex-shrink-0"><Phone size={18} /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-slate-400">Phone</span>
                  <span className="block text-brand-900 font-medium group-hover:text-brand-700 transition-colors">{CONTACT.phone}</span>
                </span>
              </a>
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-8">
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-slate-200 p-8 md:p-10">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-xs uppercase tracking-widest text-slate-500">Name *</Label>
                    <Input id="name" data-testid="contact-input-name" className={`mt-2 ${inputCls}`} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your full name" />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-xs uppercase tracking-widest text-slate-500">Company</Label>
                    <Input id="company" data-testid="contact-input-company" className={`mt-2 ${inputCls}`} value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Company name" />
                  </div>
                  <div>
                    <Label htmlFor="country" className="text-xs uppercase tracking-widest text-slate-500">Country</Label>
                    <Input id="country" data-testid="contact-input-country" className={`mt-2 ${inputCls}`} value={form.country} onChange={(e) => update("country", e.target.value)} placeholder="Country" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-500">Email *</Label>
                    <Input id="email" type="email" data-testid="contact-input-email" className={`mt-2 ${inputCls}`} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-slate-500">Phone</Label>
                    <Input id="phone" data-testid="contact-input-phone" className={`mt-2 ${inputCls}`} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+00 000 000 000" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Department</Label>
                    <Select value={form.department} onValueChange={(v) => update("department", v)}>
                      <SelectTrigger data-testid="contact-select-department" className={`mt-2 rounded-none border-slate-300 h-12 focus:ring-brand-800`}>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map((d) => (
                          <SelectItem key={d} value={d} data-testid={`dept-${d.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-6">
                  <Label htmlFor="message" className="text-xs uppercase tracking-widest text-slate-500">Message *</Label>
                  <Textarea id="message" data-testid="contact-input-message" rows={5} className="mt-2 rounded-none border-slate-300 focus-visible:ring-brand-800 focus-visible:ring-offset-0" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your facility and requirements..." />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="mt-8 group inline-flex items-center justify-center gap-2 bg-brand-800 text-white px-10 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-brand-900 transition-colors active:scale-[0.98] disabled:opacity-60"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <>Talk to Our Engineering Team <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="border-t border-slate-200">
        <div className="h-[360px] md:h-[460px] w-full grayscale hover:grayscale-0 transition-all duration-500">
          <iframe
            title="STERILTRON location"
            data-testid="contact-map"
            src="https://www.google.com/maps?q=2651%20R%C3%A9ts%C3%A1g%2C%20Pusztasz%C3%A1nt%C3%B3i%20%C3%BAt%2010.%2C%20Hungary&z=13&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </>
  );
}
